import { useEffect, useState, useContext } from 'react';
import { useNavigate } from "react-router-dom";

import { DataContext } from 'providers/data';
import { DecodeToken } from 'utils/decodeToken';
import { ParamsDataOrdersByCustomerId } from 'utils/typeServices';

import FadeAnimation from 'components/views/FadeAnimation';
import { Typography, Box, Button, Collapse } from '@mui/material';
import { ButtonRectangle, ButtonOutlined } from 'components/views/form/Button';

import { TypographyLight, BoxCase, Label, BoxSvg, Ul } from 'pages/Scheduling/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as IcShared } from 'assets/icons/ic-shared.svg';
import { ReactComponent as IcDownload } from 'assets/icons/ic-download.svg';

type Params = {
    openSchedule: ParamsDataOrdersByCustomerId;
    reschedulingFeeValue?: number;
}

const OpenedScheduling = ({ openSchedule, reschedulingFeeValue }: Params) => {
    const date = new Date();
    date.setDate(date.getDate() + 1);

    const navigate = useNavigate();
    const [showDetails, setShowDetails] = useState(false);
    const [showFrustrated, setShowFrustrated] = useState(false);
    const [isLoadingTicket, setIsLoadingTicket] = useState(false);
    const [showFrustratedScheduling, setShowFrustratedScheduling] = useState(false);

    const { selectedCar, dataGetPaymentTicketScheduling, handleGetPaymentTicketScheduling } = useContext(DataContext);

    useEffect(() => {
        if (openSchedule) {
            const dateInstallationAux = new Date(openSchedule.DT_INICIO);
            if (dateInstallationAux && date) {
                if ((date.getTime() > dateInstallationAux.getTime())) {
                    setShowFrustrated(true);
                } else {
                    setShowFrustrated(false);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSchedule]);

    useEffect(() => {
        if (dataGetPaymentTicketScheduling !== undefined) {
            setIsLoadingTicket(false);
        }

        if (dataGetPaymentTicketScheduling && dataGetPaymentTicketScheduling?.Pdf && isLoadingTicket) {
            const filePath = `data:application/pdf;base64,${dataGetPaymentTicketScheduling?.Pdf}`;
            let link = document.createElement('a');
            link.href = filePath;
            link.download = 'taxa-de-reagendamento';
            link.click();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataGetPaymentTicketScheduling]);

    return (
        <FadeAnimation>
            <Box>
                {showFrustratedScheduling ?
                    <Box sx={{ paddingTop: '5.5625rem' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography fontWeight={700} fontSize='1.125rem'>
                                Olá, Cliente Ituran!
                            </Typography>

                            <Typography fontSize='1rem' lineHeight='18px' padding='3rem 0 3.81rem' sx={{ 'b': { color: '#17A7E0', fontWeight: 400 } }}>
                                Seu reagendamento possui um intervalo <b>inferior a <strong>24 horas <br />
                                    antes da data/hora agendada,</strong></b>  por isso <b>terá uma incidência</b> <br />
                                de custo de agenda <b>de <strong>R$ {reschedulingFeeValue?.toFixed(2).replace('.', ',') ?? '50,00'}</strong></b> via boleto.
                                <br />
                                <br />
                                Não se preocupe, você pode continuar o seu reagendamento!
                            </Typography>
                        </Box>

                        <Box sx={{ marginBottom: '1.625rem', display: 'flex', justifyContent: 'center', gap: '.75rem', 'button': { width: '100%', maxWidth: '11.25rem', height: '2.5rem' } }}>
                            <ButtonOutlined name='CANCELAR' onClick={() => setShowFrustratedScheduling(false)} />
                            <ButtonRectangle name='REAGENDAR' onClick={() => { setShowFrustratedScheduling(false); navigate('reagendamento', { state: { orderId: openSchedule.CD_PEDIDO } }); }} />
                        </Box>

                        <Box>
                            <TypographyLight sx={{ marginBottom: '.5rem' }}>
                                * Regras de reagendamento:
                            </TypographyLight>
                            <Ul>
                                <li>
                                    <p>
                                        O boleto <strong>deverá</strong> ser quitado em até 3 horas antes da realização do serviço.
                                    </p>
                                </li>
                            </Ul>
                        </Box>
                    </Box>
                    :
                    <Box paddingTop='.3125rem' position='relative'>
                        <TypographyLight sx={{ marginBottom: '4px', zIndex: '999' }}>
                            Veículo
                        </TypographyLight>
                        <Box sx={{ display: 'grid', alignItems: 'center', gridTemplateColumns: { xs: '100%', md: '29% auto 29% auto 29%' } }}>
                            <BoxCase sx={{ backgroundColor: 'primary.light' }}>
                                <Typography fontWeight={700} color='white' textTransform='uppercase' sx={{
                                    fontSize: { xs: '1.125rem', md: '1rem' }
                                }}>
                                    {openSchedule.DS_PLACA || openSchedule.DS_CHASSI}
                                </Typography>
                            </BoxCase>
                            <Box sx={{ height: { xs: '2.625rem', md: '1px' }, width: { xs: '1px', md: '100%' }, margin: '0 auto', backgroundColor: 'grey.400' }} />
                            <Box>
                                {showFrustrated && openSchedule.FL_RESCHEDULING &&
                                    <Typography fontSize='1rem' color='error.main'
                                        sx={{
                                            position: 'absolute',
                                            top: { xs: '4.9rem', md: '0' },
                                            left: '0',
                                            right: '0',
                                            margin: '0 auto',
                                            textAlign: 'center',
                                            padding: { xs: '.3rem 0', md: '0' },
                                        }}
                                    >
                                        Faça seu reagendamento!
                                    </Typography>
                                }
                                <BoxCase sx={{ border: showFrustrated || [5, 7].indexOf(openSchedule.NR_STATUS) >= 0 ? '1px solid #FF0000' : '1px solid #8D9BB5' }}>
                                    <Typography fontSize='.9375rem' color={showFrustrated || [5, 7].indexOf(openSchedule.NR_STATUS) >= 0 ? 'error.main' : 'grey.400'}>
                                        Status: <strong style={{ textTransform: 'uppercase' }}>{openSchedule.DS_STATUS}</strong>
                                    </Typography>
                                </BoxCase>
                                <Box sx={{ display: { xs: 'flex', md: 'none' }, height: { xs: '1rem', md: '1px' }, width: { xs: '1px', md: 'auto' }, margin: '0 auto', backgroundColor: 'grey.400' }} />
                                {openSchedule.FL_RESCHEDULING &&
                                    <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
                                        <Button
                                            variant='outlined'
                                            sx={{
                                                borderWidth: '2px',
                                                width: '65%',
                                                height: '1.75rem',
                                                paddingTop: '8px',
                                                borderRadius: '20px',
                                                fontSize: '.9375rem',
                                                fontWeight: 700,
                                                ':hover': {
                                                    borderWidth: '2px'
                                                }
                                            }}
                                            onClick={() => showFrustrated ? setShowFrustratedScheduling(true) : navigate('reagendamento', { state: { orderId: openSchedule.CD_PEDIDO } })}
                                        >
                                            REAGENDAR
                                        </Button>
                                        *
                                    </Box>
                                }
                            </Box>
                            {openSchedule.CD_COBRANCA_TAXA_REAGENDAMENTO > 0 ?
                                <>
                                    <Box sx={{ height: { xs: '1.625rem', md: '1px' }, width: { xs: '1px', md: '100%' }, margin: '0 auto', backgroundColor: 'grey.400' }} />
                                    <Box sx={{
                                        'button': {
                                            width: '100%',
                                            height: '2.3rem',
                                            borderRadius: '5px',
                                            maxWidth: '10.625rem',
                                        },
                                        textAlign: { xs: 'center', md: 'start' }
                                    }}>
                                        {isLoadingTicket ?
                                            <ButtonRectangle loadingBtn={true} />
                                            :
                                            <Button
                                                variant="contained"
                                                startIcon={<IcDownload />}
                                                sx={{
                                                    fontSize: '.75rem',
                                                    backgroundColor: 'secondary.main',
                                                    color: 'primary.main',
                                                    textTransform: 'none',
                                                    padding: '4px 1.5rem 0',
                                                    '&:hover': {
                                                        backgroundColor: 'secondary.main',
                                                        color: 'primary.main'
                                                    }
                                                }}
                                                onClick={() => {
                                                    handleGetPaymentTicketScheduling({
                                                        CD_COBRANCA_TAXA_REAGENDAMENTO: openSchedule.CD_COBRANCA_TAXA_REAGENDAMENTO,
                                                        body: {
                                                            CPF_CNPJ: selectedCar?.NR_CNPJ_CPF || DecodeToken().document,
                                                            ID_MOTIVO: 0,
                                                            DS_PLACA: selectedCar?.PLACA || null,
                                                            CD_PARCEIRO: 1783625
                                                        }
                                                    });
                                                    setIsLoadingTicket(true);
                                                }}
                                            >
                                                Boleto Taxa de Reagendamento
                                            </Button>
                                        }
                                    </Box>
                                </>
                                :
                                <div />
                            }
                        </Box>
                        {openSchedule.FL_RESCHEDULING &&
                            <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', padding: '.75rem 0 0' }}>
                                <Button
                                    variant='outlined'
                                    sx={{
                                        borderWidth: '2px',
                                        width: '28%',
                                        height: '1.75rem',
                                        paddingTop: '8px',
                                        borderRadius: '20px',
                                        fontSize: '.9375rem',
                                        fontWeight: 700,
                                        ':hover': {
                                            borderWidth: '2px'
                                        }
                                    }}
                                    onClick={() => showFrustrated ? setShowFrustratedScheduling(true) : navigate('reagendamento', { state: { orderId: openSchedule.CD_PEDIDO } })}
                                >
                                    REAGENDAR
                                </Button>
                                *
                            </Box>
                        }

                        <Box sx={{ padding: { xs: '1.5625rem 0 1.435rem', md: '0 0 1.435rem' }, borderBottom: '1px solid #8D9BB5', marginBottom: '2.1875rem' }}>
                            <Box sx={{ gap: '3.5625rem', marginTop: '1.5625rem', display: 'flex', alignItems: 'flex-start', cursor: 'pointer', position: 'relative' }} onClick={() => setShowDetails(!showDetails)}>
                                <TypographyLight sx={{ paddingTop: '5px' }}>
                                    Informações do agendamento:
                                </TypographyLight>
                                <BoxSvg className={showDetails ? 'show' : 'hide'}>
                                    <ExpandMoreIcon fontSize='medium' />
                                </BoxSvg>
                            </Box>

                            <Collapse in={showDetails}>
                                <Box sx={{ display: 'grid', backgroundColor: 'rgba(141, 155, 181, 0.1)', padding: '1rem .6875rem', alignItems: 'flex-start', gridTemplateColumns: { xs: '1fr', md: 'auto auto 1fr' }, rowGap: '1.375rem', columnGap: '10%' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label>
                                            <p className='title'>
                                                Data do agendamento
                                            </p>
                                            <p className='description'>
                                                {new Date(openSchedule.DT_INICIO).toLocaleDateString('pt-BR')}<br />
                                                {new Date(openSchedule.DT_INICIO).toLocaleTimeString()}
                                            </p>
                                        </Label>
                                        <Label sx={{ display: { xs: 'block', md: 'none' } }}>
                                            {openSchedule.NM_PRODUTO &&
                                                <>
                                                    <p className='title'>
                                                        Produto
                                                    </p>
                                                    <p className='description'>
                                                        {openSchedule.NM_PRODUTO}
                                                    </p>
                                                </>
                                            }
                                        </Label>
                                    </Box>
                                    <Label sx={{ display: { xs: 'none', md: 'block' } }}>
                                        {openSchedule.NM_PRODUTO &&
                                            <>
                                                <p className='title'>
                                                    Produto
                                                </p>
                                                <p className='description'>
                                                    {openSchedule.NM_PRODUTO}
                                                </p>
                                            </>
                                        }
                                    </Label>

                                    <Label>
                                        <p className='title'>
                                            Loja agendamento
                                        </p>
                                        <p className='description' style={{ lineHeight: '1.3rem' }}>
                                            {openSchedule.NM_LOJA} <br />
                                            {openSchedule?.NM_ENDERECO} - {openSchedule?.NR_NUMERO} - {openSchedule?.NM_BAIRRO} - {openSchedule?.NM_CIDADE}
                                            <a href={`http://maps.google.com/?q=${openSchedule?.NM_CIDADE},${openSchedule?.NM_ENDERECO},${openSchedule?.NM_BAIRRO},${openSchedule?.NR_NUMERO}`} target='blank'>
                                                <IcShared />
                                            </a>
                                        </p>
                                    </Label>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label>
                                            <p className='title'>
                                                Data do pedido
                                            </p>
                                            <p className='description'>
                                                {new Date(openSchedule.DATA_PEDIDO).toLocaleDateString('pt-BR')}
                                            </p>
                                        </Label>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    </Box>
                }
            </Box>
        </FadeAnimation >
    );
}

export default OpenedScheduling;